import { useEffect, useState } from 'react';
import {
  Box, Button, Chip, IconButton, InputAdornment, Link, Slider, Stack, TextField,
  Tooltip, Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import TimerOffOutlinedIcon from '@mui/icons-material/TimerOffOutlined';

import { PlacePicker } from './PlacePicker';
import { StayRow } from './StayRows';
import type { Category, Poi } from '../api/types';

/**
 * Everything a traveller can say about staying away from somewhere.
 *
 * Shared by the single-day form, the trip, and each day of a trip — one
 * component rather than three that resemble each other, because avoidance is
 * the part of this app with the most rules per pixel and three copies would
 * drift within a week. A caller that has no map pins simply does not pass
 * any, and the pin row disappears.
 */

/**
 * A category picker used as a filter, not as a selection.
 *
 * Labelled for what it does rather than for what it holds: a bare "Category"
 * above a place search reads as another thing to fill in, when it only
 * narrows the search below it.
 */
export function CategoryFilter({
  categories, value, onChange, label = 'Filter by category',
}: {
  categories: Category[];
  value: string;
  onChange: (categoryKey: string) => void;
  label?: string;
}) {
  if (categories.length === 0) return null;
  const selected = categories.find((c) => c.key === value) ?? null;

  return (
    <Autocomplete
      size="small"
      fullWidth
      // Without this the control shrinks to its content inside a flex row,
      // truncating the label to "Filter by cate...".
      sx={{ flex: 1, minWidth: 0 }}
      options={categories}
      value={selected}
      onChange={(_, next) => onChange(next?.key ?? '')}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      slotProps={{ listbox: { sx: { maxHeight: 280 } } }}
      renderOption={(props, option) => {
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
        return (
          <Box component="li" key={key} {...rest}>
            <Typography variant="body2" sx={{ flex: 1 }} noWrap>{option.label}</Typography>
            <Typography variant="caption" color="text.secondary">{option.poiCount}</Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="All categories"
          helperText="Narrows the search below"
        />
      )}
    />
  );
}

/**
 * The kinds of site a round draws from — a choice, not a filter.
 *
 * The single-select above it only narrowed the search box beneath, which
 * looked like the place to say what the round was for and did nothing of the
 * sort: what the planner actually reads is `interestCategoryKeys`, and the
 * only way to set them was through the assistant. So this holds several,
 * feeds the request, and narrows the search below as the filter did.
 *
 * Empty is not "none" — it means the round is shaped by its emphasis or its
 * route style instead, which is what most rounds want.
 */
export function CategoryChoice({
  /*
    Short enough to fit the field.

    A label is the field's own width, and an outlined MUI label does not
    wrap — it truncates. "What kinds of site should we look for?" became
    "What kinds of site should w…", which is worse than the plain noun it
    replaced. The question survives; the sentence does not.
  */
  categories, value, onChange, label = 'What kinds of site?', helperText,
  dwellMinutes = {}, onDwellChange,
}: {
  categories: Category[];
  value: string[];
  onChange: (categoryKeys: string[]) => void;
  label?: string;
  helperText?: string;
  /** This round's stay time per kind, keyed as the request is. */
  dwellMinutes?: Record<string, number>;
  /** Omit and the rows show what each kind takes without offering to change it. */
  onDwellChange?: (categoryKey: string, minutes: number | null) => void;
}) {
  /*
   * One at a time, added deliberately.
   *
   * A multi-select held every choice inside the field, which reads as a
   * filter and leaves nowhere to say anything ABOUT a choice. Picked and
   * added, each kind gets a row of its own — and a row is somewhere a stay
   * time can live.
   */
  const [pending, setPending] = useState<Category | null>(null);
  if (categories.length === 0) return null;

  const chosen = value
    .map((key) => categories.find((c) => c.key === key))
    .filter((c): c is Category => Boolean(c));
  /* Whether the whole list is already set to be passed without stopping. */
  const allPassing = chosen.length > 0 && chosen.every((c) => dwellMinutes[c.key] === 0);

  const add = () => {
    if (!pending || value.includes(pending.key)) return;
    onChange([...value, pending.key]);
    /*
      A kind added to a round that stops nowhere does not stop either.

      Otherwise "pass all by" quietly stops meaning what it says the moment
      anything is added, and the one row with a stay on it is the one nobody
      asked for — the round then takes half an hour somewhere and comes back
      short, with nothing on screen admitting why.
    */
    if (allPassing) onDwellChange?.(pending.key, 0);
    setPending(null);
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <Autocomplete
          size="small"
          fullWidth
          sx={{ flex: 1, minWidth: 0 }}
          options={categories.filter((c) => !value.includes(c.key))}
          value={pending}
          onChange={(_, next) => setPending(next)}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(a, b) => a.key === b.key}
          slotProps={{ listbox: { sx: { maxHeight: 280 } } }}
          renderOption={(props, option) => {
            const { key, ...rest } =
              props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
            return (
              <Box component="li" key={key} {...rest}>
                <Typography variant="body2" sx={{ flex: 1 }} noWrap>{option.label}</Typography>
                <Typography variant="caption" color="text.secondary">{option.poiCount}</Typography>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={value.length === 0 ? 'Emphasis or route style decides' : 'Add another'}
              /*
                Only what a caller asks for.

                This carried two sentences of its own — "Sites are drawn from
                these. Blank leaves it to the emphasis or style." under an
                empty field, and "Sites are drawn from these, and the search
                below is narrowed to them." once it had something in it. Both
                restated the label and the placeholder beside them, on a
                control that now sits under a question saying the same thing
                a third time.

                The exclusions panel still passes its own, which is the case
                this prop exists for.
              */
              helperText={helperText}
              // Enter adds, as it would in any list somebody is building.
              onKeyDown={(event) => {
                if (event.key === 'Enter' && pending) {
                  event.preventDefault();
                  add();
                }
              }}
            />
          )}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={!pending}
          onClick={add}
          sx={{ mt: 0.25, flexShrink: 0 }}
        >
          Add
        </Button>
      </Stack>

      {chosen.length > 0 && (
        <Box sx={{ px: 0.5, pt: 1 }}>
          {/*
            The stay rows explain themselves.

            "Blank stays for the usual time. 0 passes without stopping." sat
            above them as a third line of guidance under a heading and a
            caption that had already said what this box is for, and each row
            below prints its own "usually 45 min". Kept only where there are
            no stay boxes to read it off.
          */}
          {/*
            A heading that says what the boxes beside each row are FOR.

            The rows carried a number field and a count with nothing naming
            either, so "126 here · usually 45 min" beside an editable 45 read
            as a statistic somebody was being asked to correct. The question
            above them says whose time it is; the row says how many there are
            to draw from and what they usually take.
          */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}
          >
            <Typography variant="caption" color="text.secondary">
              {onDwellChange
                ? 'How long at each one?'
                : 'Sites are drawn from these.'}
            </Typography>

            {/*
              The same answer for every kind, in one click.

              An emphasis fills this list with nine or ten kinds, and "go
              round without stopping" is an answer about the whole round
              rather than about museums in particular — ten clicks to say one
              thing. The row controls stay, for the mixed case.
            */}
            {onDwellChange && chosen.length > 1 && (
              <Button
                size="small"
                sx={{ textTransform: 'none', flexShrink: 0 }}
                startIcon={allPassing
                  ? <TimerOutlinedIcon fontSize="small" />
                  : <TimerOffOutlinedIcon fontSize="small" />}
                onClick={() => chosen.forEach(
                  (category) => onDwellChange(category.key, allPassing ? null : 0),
                )}
              >
                {allPassing ? 'Stay at all' : 'Pass all by'}
              </Button>
            )}
          </Stack>
          <Stack spacing={0.75}>
            {chosen.map((category) => (
              <StayRow
                key={category.key}
                label={category.label}
                /*
                  How long, and nothing else.

                  The count of places of this kind in the city rode along
                  here — "23 in this city · usually 45 min" — which is a fact
                  about the catalogue, not about the day being planned, and
                  it pushed the part that matters far enough right to be
                  truncated. The question above asks how long; this answers
                  it.
                */
                hint={`usually ${category.defaultDwellMinutes} min`}
                fallback={category.defaultDwellMinutes}
                value={dwellMinutes[category.key]}
                onChange={(minutes) => onDwellChange?.(category.key, minutes)}
                onRemove={() => onChange(value.filter((key) => key !== category.key))}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

/**
 * Which of those to favour, once there are enough to choose between.
 *
 * Three or more kinds of site stand as equals in the ranking, and the
 * optimiser then takes whatever is nearest — which is how a round asking for
 * hospitals, police, fire stations, fuel, parking and charging comes back as
 * six petrol stations. Naming one or two here tilts the ranking without
 * promising a share, so the others still appear.
 *
 * Below three there is nothing to tilt, so it is not shown at all.
 */
export function BoostChoice({
  categories, chosen, value, onChange,
}: {
  categories: Category[];
  /** The keys already chosen to draw from — the only things worth favouring. */
  chosen: string[];
  value: string[];
  onChange: (categoryKeys: string[]) => void;
}) {
  /*
   * Below two kinds there is nothing to rank, so there is no control.
   *
   * This has moved twice, and the middle position was the wrong one. It
   * first hid below THREE kinds, on the grounds that favouring among two
   * says little. That was relaxed to always-on, reasoning that a control
   * appearing at a threshold reads as a control that was missing — which is
   * a fair worry at three, where the feature does still do something.
   *
   * At one kind it does not. Ranking a list against itself is a no-op, and
   * the control could only explain that it had nothing to do, which it did,
   * in a helper line under an empty box. Two is the first number where the
   * word "favour" means anything at all.
   */
  if (chosen.length < 2) return null;

  const labelFor = (key: string) => categories.find((c) => c.key === key)?.label ?? key;

  return (
    <Autocomplete
      multiple
      size="small"
      fullWidth
      sx={{ flex: 1, minWidth: 0 }}
      options={chosen}
      // Only ever the ones still chosen: a favourite whose category has been
      // taken off the list above is not a preference, it is a leftover.
      value={value.filter((key) => chosen.includes(key))}
      onChange={(_, next) => onChange(next)}
      getOptionLabel={labelFor}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Favor which of them?"
          placeholder={value.length === 0 ? 'All equal' : ''}
          helperText="Ranked higher among the kinds above, so they turn up more often. Nothing is promised."
        />
      )}
    />
  );
}

/**
 * One avoided thing and the distance to keep from it.
 *
 * Empty means "use the default", which is shown as the placeholder rather
 * than filled in — a box holding the default it inherited looks like a choice
 * somebody made, and there would then be no way to say "back to default".
 */
export function AvoidDistanceRow({
  label, hint, fallback, value, colour, onChange, onRemove, onOpen,
}: {
  label: string;
  hint: string;
  fallback: number;
  value: number | undefined;
  /** The colour this category is drawn in, so the row and the map agree. */
  colour: string;
  onChange: (metres: number | null) => void;
  /**
   * Takes it off the list. This row is the only place that can — the picker
   * above holds one candidate at a time and keeps no chips of its own.
   */
  onRemove?: () => void;
  /** Opens the place's full record. A category has none, so it stays plain. */
  onOpen?: () => void;
}) {
  /*
   * What is typed, held as typed, and only interpreted when the field is
   * left.
   *
   * Clamping on every keystroke made the field impossible to edit: changing
   * 500 to 800 begins by typing "8", which was immediately read as 8 metres,
   * clamped up to the 50 m floor, and written back — so the next keystroke
   * appended to "50" and the value never became what anybody meant. The
   * bounds still apply, on commit, where they cannot fight the typist.
   */
  const [text, setText] = useState(value === undefined ? '' : String(value));
  useEffect(() => {
    setText(value === undefined ? '' : String(value));
  }, [value]);

  const commit = () => {
    const raw = text.trim();
    if (raw === '') {
      onChange(null);
      return;
    }
    const next = Number(raw);
    if (!Number.isFinite(next)) {
      // Not a number at all: put back whatever is actually in force.
      setText(value === undefined ? '' : String(value));
      return;
    }
    /*
      Clamped to what is usable, not to a round number.

      The floor was 50 m, so somebody asking for 40 was quietly given 50 and
      no reason why. There is nothing wrong with 40: a keep-clear distance is
      an instruction about one place, and a tight one around a single entrance
      is as legitimate as a wide one around a district. Only a distance that
      cannot mean anything — nought, or negative — is refused.
    */
    const bounded = Math.min(5000, Math.max(1, Math.round(next)));
    setText(String(bounded));
    onChange(bounded);
  };

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <Box
        sx={{
          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
          bgcolor: colour, border: '1px solid rgba(0,0,0,.15)',
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {onOpen ? (
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={onOpen}
            sx={{ display: 'block', maxWidth: '100%', textAlign: 'left', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {label}
          </Link>
        ) : (
          <Typography variant="body2" noWrap>{label}</Typography>
        )}
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {hint}
        </Typography>
      </Box>
      <TextField
        size="small"
        type="number"
        placeholder={String(fallback)}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
        slotProps={{
          /*
            `step` is what the arrow keys move by, and 50 made the keyboard
            unable to reach 40 at all. Ten is fine enough to land on anything
            anybody means and still coarse enough to be worth holding down.
          */
          htmlInput: { min: 1, max: 5000, step: 10, 'aria-label': `Distance from ${label}` },
          input: { endAdornment: <InputAdornment position="end">m</InputAdornment> },
        }}
        sx={{ width: 108, flexShrink: 0 }}
      />
      {onRemove && (
        <Tooltip title={`Stop avoiding ${label}`}>
          <IconButton size="small" onClick={onRemove} sx={{ flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

export function AvoidancePanel({
  cityId, categories, filter, onFilterChange,
  avoid, onAvoidChange, avoidCategories, onAvoidCategoriesChange, visitingCategories = [],
  avoidDistanceM, onAvoidDistanceChange,
  colourFor,
  avoidDistanceByPoiId = {}, avoidDistanceByCategoryKey = {}, onAvoidDistanceOverride,
  pinRow,
  onExternalPick, onShowDetails,
  maxPlaces = 12,
}: {
  cityId: string;
  categories: Category[];
  filter: string;
  onFilterChange: (key: string) => void;
  avoid: Poi[];
  onAvoidChange: (pois: Poi[]) => void;
  avoidCategories: string[];
  onAvoidCategoriesChange: (keys: string[]) => void;
  /**
   * The kinds of site this round draws its stops from.
   *
   * Passed in so the same kind cannot be both visited and avoided. Asking for
   * hospitals and then excluding every hospital is two instructions that
   * cancel, and the planner obeys the exclusion: the round comes back empty,
   * or without the thing it was for, and nothing on screen says which of the
   * two answers won.
   */
  visitingCategories?: string[];
  avoidDistanceM: number;
  onAvoidDistanceChange: (metres: number) => void;
  colourFor: (categoryKey: string) => string;
  /** Per-place and per-category overrides. Omit to offer none. */
  avoidDistanceByPoiId?: Record<string, number>;
  avoidDistanceByCategoryKey?: Record<string, number>;
  onAvoidDistanceOverride?: (
    scope: 'poi' | 'category', key: string, metres: number | null,
  ) => void;
  /** Chips for spots marked on the map. Callers without a map pass nothing. */
  pinRow?: React.ReactNode;
  onExternalPick?: (place: { name: string; lat: number; lng: number }) => void;
  /** Opens a place's full record from its row, as every other list does. */
  onShowDetails?: (poiId: string) => void;
  maxPlaces?: number;
}) {
  const avoidable = categories.filter((c) => c.isAvoidable);
  const chosen = avoidable.find((c) => c.key === filter);
  /*
   * Already being visited, so it cannot also be avoided.
   *
   * Refused rather than warned about: the two answers contradict, and a
   * warning would leave the operator to work out which one the planner acted
   * on. Taking it off the list of sites is the way to stop visiting it.
   */
  const alsoVisiting = Boolean(filter) && visitingCategories.includes(filter);
  const alreadyExcluded = Boolean(filter) && avoidCategories.includes(filter);

  return (
    <>
      {/* Picked again after the fact, the button has to say so. */}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', width: '100%' }}>
        <CategoryFilter
          categories={avoidable}
          value={filter}
          onChange={onFilterChange}
        />
        <Tooltip
          title={
            alsoVisiting
              ? `This round is drawing its stops from ${chosen?.label ?? 'this kind'}. `
                + 'Take it off "what this round is for" before excluding it.'
              : alreadyExcluded
                ? 'Already kept clear of — it is in the list below'
                : filter
                  ? `Exclude all ${chosen?.poiCount ?? ''} of them as one rule`
                  : 'Choose a category first, then exclude all of it or search within it'
          }
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              disabled={!filter || alreadyExcluded || alsoVisiting}
              /*
                The filter is cleared on the way out.

                Pressing this left the category sitting in the field with the
                button greyed beside it, still reading "Exclude all" — which
                looks like a press that did nothing. Clearing it moves the
                kind visibly out of the picker and into the rules below,
                which is exactly what just happened to it. Narrowing a search
                to a category the round now keeps clear of was never useful
                anyway.
              */
              onClick={() => {
                onAvoidCategoriesChange([...avoidCategories, filter]);
                onFilterChange('');
              }}
              sx={{ whiteSpace: 'nowrap', height: 40 }}
            >
              {alreadyExcluded ? 'Excluded' : 'Exclude all'}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {avoidCategories.length > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }} useFlexGap>
          {avoidCategories.map((key) => {
            const category = categories.find((c) => c.key === key);
            return (
              <Chip
                key={key}
                size="small"
                color="warning"
                variant="filled"
                label={`All ${category?.label ?? key} (${category?.poiCount ?? '?'})`}
                onDelete={() => onAvoidCategoriesChange(avoidCategories.filter((k) => k !== key))}
              />
            );
          })}
        </Stack>
      )}

      {pinRow}

      <PlacePicker
        cityId={cityId}
        label="Add a single site"
        placeholder={filter ? 'Search within this category' : 'Search any place'}
        {...(filter ? { categoryKeys: [filter] } : {})}
        value={avoid}
        onChange={onAvoidChange}
        max={maxPlaces}
        {...(onExternalPick ? { onExternalPick } : {})}
      />

      {/*
        Always shown, not revealed once something is avoided. It reads as a
        setting for this whole section, so hiding it makes people think
        choosing a category in the filter above should have produced it.
      */}
      <Box sx={{ px: 0.5, pt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Hold at least {avoidDistanceM} m from every site below
        </Typography>
        <Slider
          value={avoidDistanceM}
          onChange={(_, value) => onAvoidDistanceChange(value as number)}
          min={100}
          max={2000}
          step={100}
          marks={[
            { value: 100, label: '100 m' },
            { value: 1000, label: '1 km' },
            { value: 2000, label: '2 km' },
          ]}
          size="small"
        />
      </Box>

      {/*
        What is being avoided, and how far from each.

        The list doubles as the record of what has been chosen, since the
        picker above keeps no chips: this is where something comes off the
        list. One radius also rarely suits everything on it — a nightclub
        might want 200 m and a motorway junction 1 km — so each row can be
        given its own, and blank keeps the slider's.
      */}
      {onAvoidDistanceOverride && (avoid.length > 0 || avoidCategories.length > 0) && (
        <Box sx={{ px: 0.5, pt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Kept clear of — blank holds the distance above
          </Typography>
          <Stack spacing={0.75}>
            {avoidCategories.map((key) => (
              <AvoidDistanceRow
                key={`cat-${key}`}
                label={categories.find((c) => c.key === key)?.label ?? key}
                hint="every place in this category"
                fallback={avoidDistanceM}
                value={avoidDistanceByCategoryKey[key]}
                colour={colourFor(key)}
                onChange={(metres) => onAvoidDistanceOverride('category', key, metres)}
                onRemove={() =>
                  onAvoidCategoriesChange(avoidCategories.filter((k) => k !== key))}
              />
            ))}
            {avoid.map((poi) => (
              <AvoidDistanceRow
                key={`poi-${poi.id}`}
                label={poi.name}
                hint={poi.categoryLabel}
                fallback={avoidDistanceByCategoryKey[poi.categoryKey] ?? avoidDistanceM}
                value={avoidDistanceByPoiId[poi.id]}
                colour={colourFor(poi.categoryKey)}
                onChange={(metres) => onAvoidDistanceOverride('poi', poi.id, metres)}
                onRemove={() => {
                  onAvoidDistanceOverride('poi', poi.id, null);
                  onAvoidChange(avoid.filter((p) => p.id !== poi.id));
                }}
                {...(onShowDetails ? { onOpen: () => onShowDetails(poi.id) } : {})}
              />
            ))}
          </Stack>
        </Box>
      )}
    </>
  );
}
